import { lodestarColors } from "@smartsheet/design-tokens";
import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { CloseIcon } from "@smartsheet/ursa-icons";
import React, { CSSProperties, forwardRef, KeyboardEvent, KeyboardEventHandler, MouseEvent, ReactNode } from "react";
import { sizes } from "../../../constants/spacing";
import { I18nContextProvider, I18nProviderProps, useI18n } from "../../../i18n/i18n";
import { useTheme } from "../../../theme/utils/useTheme";
import { getTextContent } from "../../../util/reactHelpers";
import { useOverflowTooltip } from "../../../util/useOverflowTooltip";
import { Tooltip, TooltipBody } from "../../tooltip";
import { ChipLabel } from "../components/ChipLabel";
import { injectCatalog } from "../i18n/ChipCatalog";
import { ChipContainer } from "../styled/ChipContainer";
import { ChipRemoveButton } from "../styled/ChipRemoveButton";
import { chipCustomCssProperties, getTokenForColor } from "../styled/chipConstants";
import { ChipColorVariant } from "../types";
import { SharedChipProps } from "../types";

const elementIsScrollable = (elem: HTMLElement | null) => {
    return !!elem && (elem.offsetHeight < elem.scrollHeight || elem.offsetWidth < elem.scrollWidth);
};

export interface BaseChipProps extends SharedChipProps, I18nProviderProps {
    /**
     * The standard color to pick from.
     */
    color: ChipColorVariant;

    /**
     * Stuff that renders between the left (in LTR) side of the chip and the text.
     */
    contentBefore?: ReactNode;

    /**
     * Optional prop to make the chip a button element.
     */
    isAButton?: boolean;
}

export const BaseChipBase = forwardRef<HTMLDivElement, Omit<BaseChipProps, "locale">>(
    (
        {
            "aria-label": providedAriaLabel,
            "aria-labelledby": ariaLabelledby,
            "aria-describedby": ariaDescribedBy,
            children,
            className,
            color: colorVariant = "neutral",
            onKeyDown,
            onRemove,
            clientId,
            isReadonly = false,
            contentBefore,
            omitTooltip = false,
            size = "medium",
            overrides,
            isAButton = false,
            role: providedChipRole,
            ...rest
        },
        ref
    ) => {
        const { i18n } = useI18n();
        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        const isInteractive = !isReadonly;

        const handleRemove = (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
            if (isInteractive) {
                onRemove?.(event);
            }
        };
        const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
            // allow navigation even if chip is read only
            onKeyDown?.(event);
            if (isInteractive) {
                switch (event.key) {
                    case "Backspace":
                    case "Delete":
                        handleRemove(event);
                }
            }
        };

        // cannot have a button inside a button (or a tabindex inside a tabindex)
        // If there is an onRemove handler, assume the Chip is a button and the Remove Icon is just a note
        //  so the backspace/delete key can be processed on the Chip and not just the Remove Icon.
        const defaultChipRole = providedChipRole || (onRemove ? "button" : "note");
        const chipRole = isAButton ? "button" : defaultChipRole;
        // if the Chip is a button, make the Chip (the div) focusable
        const defaultChipTabIndex = chipRole === "button" ? 0 : undefined;
        const chipTabIndex = rest.tabIndex !== undefined ? rest.tabIndex : defaultChipTabIndex;

        // if chip is a role, make the remove icon a note.
        const defaultButtonRole = chipRole === "button" || chipTabIndex !== undefined ? "note" : "button";
        const removeButtonRole = overrides?.removeButton?.role || defaultButtonRole;
        // give the Remove Icon a tabindex if it is a button
        const defaultRemoveButtonTabIndex = removeButtonRole === "button" ? 0 : undefined;
        const removeButtonTabIndex =
            overrides?.removeButton?.tabIndex !== undefined
                ? overrides?.removeButton?.tabIndex
                : defaultRemoveButtonTabIndex;

        const { showOverflowTooltip, tooltipProps, targetProps, elementToMeasureRef } = useOverflowTooltip<
            HTMLSpanElement,
            HTMLDivElement
        >({
            tooltipTargetRef: ref,
            elementIsOverflowing: elementIsScrollable,
            onBlur: rest.onBlur,
            onFocus: rest.onFocus,
            onMouseEnter: rest.onMouseEnter,
            onMouseLeave: rest.onMouseLeave,
            isDisabled: omitTooltip,
        });

        const colorProperties = getTokenForColor(colorVariant, themeMode);
        const ariaLabel = providedAriaLabel || getTextContent(children);

        return (
            <ChipContainer
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledby}
                aria-describedby={ariaDescribedBy}
                aria-disabled={isReadonly || undefined}
                data-client-id={clientId}
                className={className}
                isReadonly={isReadonly}
                themeMode={themeMode}
                size={size}
                role={chipRole}
                tabIndex={chipTabIndex}
                {...rest}
                onKeyDown={handleKeyDown}
                // Don't add tooltip target event handlers if there is no overflow tooltip
                {...(showOverflowTooltip && !omitTooltip ? targetProps : {})}
                // downshift needs a ref to handle keyboard navigation so always pass the ref along even
                // if we are not showing/handling a tooltip.
                ref={targetProps.ref}
                style={
                    {
                        // We'll apply these properties here so we don't have to generate as much
                        // CSS for the Chips. This is good for readability and performance.
                        [chipCustomCssProperties.text]: `var(--sds--chip--label--color, ${color(
                            colorProperties.text,
                            lodestarColors.neutralDark40
                        )})`,
                        [chipCustomCssProperties.backgroundRest]: color(
                            colorProperties.backgroundRest,
                            lodestarColors.buttonDefault
                        ),
                        [chipCustomCssProperties.backgroundHover]: color(
                            colorProperties.backgroundHover,
                            lodestarColors.buttonDefault
                        ),
                        [chipCustomCssProperties.backgroundActive]: color(
                            colorProperties.backgroundActive,
                            lodestarColors.buttonDefault
                        ),
                        ...rest.style,
                    } as CSSProperties
                }
            >
                {contentBefore}
                <ChipLabel
                    ref={elementToMeasureRef}
                    size={size}
                    // We're going to apply our own here.
                    omitTooltip
                >
                    {children}
                </ChipLabel>
                {!isReadonly && onRemove ? (
                    <ChipRemoveButton
                        isReadonly={isReadonly}
                        aria-label={i18n._(
                            /*i18n*/ {
                                comment: "Remove chip",
                                id: "Remove {label}",
                                values: { label: ariaLabel },
                            }
                        )}
                        role={removeButtonRole}
                        tabIndex={removeButtonTabIndex}
                        onClick={handleRemove}
                        themeMode={themeMode}
                        size={size}
                    >
                        <CloseIcon size={sizes.medium} color="currentColor" />
                    </ChipRemoveButton>
                ) : null}
                {showOverflowTooltip && !omitTooltip && (
                    <Tooltip {...tooltipProps}>
                        <TooltipBody>{children}</TooltipBody>
                    </Tooltip>
                )}
            </ChipContainer>
        );
    }
);

BaseChipBase.displayName = "BaseChipBase";

export const BaseChip = forwardRef<HTMLDivElement, BaseChipProps>(({ locale = "en", ...props }, ref) => {
    injectCatalog();
    return (
        <I18nContextProvider locale={locale}>
            <BaseChipBase {...props} ref={ref} />
        </I18nContextProvider>
    );
});

BaseChip.displayName = "BaseChip";
